import React from 'react';
import { Modal, Typography, Divider, Badge, Timeline, Row, Col, Space, Button, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Asset } from '../../types';
import AuditTrail from '../audittrail/audittrail';
import dayjs from 'dayjs';
import moment from 'moment';

const { Title, Text } = Typography;

/** Labels for the valuation bases, matching the backend enum. */
const BASIS_LABELS: Record<string, string> = {
  market_quoted: 'Market quote (quantity × price)',
  attested: 'Stated by a third party',
  appraised: 'Appraisal or estimate',
  statement: 'Periodic statement',
};

/** Labels for the statutory exempt-property categories. */
const EPTL_LABELS: Record<string, string> = {
  household: 'Household goods, furniture, clothing, jewellery',
  books_media: 'Books, family pictures, religious books, media',
  farm: 'Farm and domestic animals, machinery, tractors',
  vehicle: 'One motor vehicle',
  money: 'Money, bank accounts, marketable securities',
  child_setoff: 'Set-off for a child under 21',
};

const money = (value?: number | string | null): string =>
  value == null || value === '' ? '—' : `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Shares arrive as fractions of 1. */
const percent = (value?: number | string | null): string =>
  value == null || value === '' ? '—' : `${(Number(value) * 100).toFixed(2)}%`;


interface AssetDetailsProps {
  visible: boolean;
  onClose: () => void;
  asset: Asset | null;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: string) => void;
}

const AssetDetails: React.FC<AssetDetailsProps> = ({ visible, onClose, asset, onEdit, onDelete }) => {
  if (!asset) return null;

  // Confirm delete action
  const handleDelete = () => {
    onDelete(asset.uid);
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      bodyStyle={{
        padding: '24px',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}
      style={{ top: 20, right: 20, position: 'fixed'}}
      closable
      mask={false}
      maskClosable
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Action Buttons */}
        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>{asset.uid}</Title>
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(asset)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this asset?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>
        
        <Text type="secondary">Asset Details</Text>
        <Divider />

        {/* Basic Information Section */}
        <Title level={5}>Basic Information</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Asset Name:</Text>
            <br />
            <Text>{asset.name}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Asset Type:</Text>
            <br />
            <Text>{asset.category}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Asset Location:</Text>
            <br />
            <Text>{asset.location}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Date Added:</Text>
            <br />
            <Text>{moment(asset.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
          </Col>
        </Row>
        <Row style={{ marginTop: '16px' }}>
          <Col span={24}>
            <Text strong>Description:</Text>
            <br />
            <Text>{asset.description}</Text>
          </Col>
        </Row>

        <Divider />

        {/* Financial Details Section */}
        <Title level={5}>Financial Details</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Value entered:</Text>
            <br />
            <Text>{money(asset.originalMarketValue)}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Market Value:</Text>
            <br />
            <Text>{money(asset.marketValue)}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Estimated Value:</Text>
            <br />
            <Text>{money(asset.estimatedValue)}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>After a {asset.discountToMarket ?? 0}% market discount</Text>
          </Col>
          <Col span={12}>
            <Text strong>Net of Lien:</Text>
            <br />
            <Text>{money(asset.netOfLien)}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Valued by:</Text>
            <br />
            <Text>{BASIS_LABELS[asset.valuationBasis as string] ?? asset.valueType}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Value Source:</Text>
            <br />
            <Text>{asset.valueSource}</Text>
          </Col>
        </Row>

        <Divider />

        {/* Lease & Liens Section */}
        <Title level={5}>Lease & Liens</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Lease?</Text>
            <br />
            <Text>{asset.lease ? 'Yes' : 'No'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Lien:</Text>
            <br />
            <Text>{asset.liens}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Net Lien:</Text>
            <br />
            <Text>{money(asset.netOfLien)}</Text>
          </Col>
        </Row>

        <Divider />

        {/* Ownership & Legal Status Section */}
        <Title level={5}>Ownership & Legal Status</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Covered in Will?</Text>
            <br />
            <Text>{asset.coveredInWill ? 'Yes' : 'No'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Jointly Owned?</Text>
            <br />
            <Text>{asset.jointlyOwned ? 'Yes' : 'No'}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Claimed as exempt family property:</Text>
            <br />
            <Text>{EPTL_LABELS[asset.eptlCategory as string] ?? 'Not claimed'}</Text>
            {asset.EPTLExemptionAmount ? (
              <>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {money(asset.EPTLExemptionAmount)} set aside
                </Text>
              </>
            ) : null}
          </Col>
          <Col span={12}>
            <Text strong>Small Estate:</Text>
            <br />
            <Text>{asset.isSmallEstate ? 'Yes' : 'No'}</Text>
          </Col>
        </Row>

        <Divider />

        {/* Additional Valuation & Distribution Section */}
        <Title level={5}>Additional Valuation & Distribution</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Realised Value (External Sale):</Text>
            <br />
            <Text>{asset.realisedExternalSale}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Discount To Market:</Text>
            <br />
            <Text>{asset.discountToMarket}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Internal Distribution Value:</Text>
            <br />
            <Text>{asset.internalDistributionAmount}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Estate Value:</Text>
            <br />
            <Text>{asset.estateValue}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Estate Value if Jointly Owned:</Text>
            <br />
            <Text>{asset.estateValueIfJointlyOwned}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Counted in Probate Administration:</Text>
            <br />
            <Text>{money(asset.toBeCountedInProbate)}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Family Discount %:</Text>
            <br />
            <Text>{asset.familyDiscount}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Family Discount Value:</Text>
            <br />
            <Text>{money(asset.familyDiscountedValue)}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Non-cash share of estate:</Text>
            <br />
            <Text>{percent(asset.percantageOfEstateAtDiscount)}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Share of estate after discount:</Text>
            <br />
            <Text>{percent(asset.shareOfTotalEstateDiscount)}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>Sold?</Text>
            <br />
            <Text>{asset.sold ? 'Yes' : 'No'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Cash Received if Sold:</Text>
            <br />
            <Text>{asset.cashReceived}</Text>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text strong>% Away From Estimated Value:</Text>
            <br />
            <Text>{asset.percentageAwayFromEstValue}</Text>
          </Col>
          <Col span={12}>
            <Text strong>EPTL Residual Estate:</Text>
            <br />
            <Text>{money(asset.residualEPTLExemptionAmount)}</Text>
          </Col>
        </Row>

        <Divider />

        {/* Edit History Timeline */}
        <Title level={5}>Edit History</Title>
        <AuditTrail />
      </Space>
    </Modal>
  );
};

export default AssetDetails;
